import { useEffect, useRef } from "react";
import { matrices } from "./matrices";

const useRotatingAnimation = (numDots: number, duration: number) => {
  const dotsRef = useRef<SVGGElement[]>([]);

  useEffect(() => {
    let start: number | null = null;

    const animate = (timestamp: number) => {
      if (!start) start = timestamp;
      const progress = (timestamp - start) / duration;

      for (const [index, dot] of dotsRef.current.entries()) {
        if (dot) {
          const transformMatrix = getTransformMatrix(index, progress);
          dot.setAttribute("transform", transformMatrix);
        }
      }

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        start = null;
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);

    return () => {
      start = null;
    };
  }, [duration]);

  const getTransformMatrix = (index: number, progress: number) => {
    const matrix = matrices[index % matrices.length];
    const controlPoint = [
      (matrix.start[0] + matrix.end[0]) / 2,
      (matrix.start[1] + matrix.end[1]) / 2,
      (matrix.start[2] + matrix.end[2]) / 2,
      (matrix.start[3] + matrix.end[3]) / 2,
      (matrix.start[4] + matrix.end[4]) / 2,
      (matrix.start[5] + matrix.end[5]) / 2,
    ];

    const interpolate = (
      startValue: number,
      controlValue: number,
      endValue: number,
      t: number,
    ) =>
      (1 - t) * (1 - t) * startValue + 2 * (1 - t) * t * controlValue + t * t * endValue;

    const interpolatedMatrix = matrix.start.map((startValue, i) => {
      const controlValue = controlPoint[i];
      const endValue = matrix.end[i];
      return interpolate(startValue, controlValue, endValue, progress);
    });

    const startAngle = 0;
    const endAngle = 45;
    const angle = startAngle + (endAngle - startAngle) * progress;

    return `matrix(${interpolatedMatrix.join(",")}) rotate(${angle})`;
  };

  return { dotsRef, matrices };
};

export default useRotatingAnimation;
